import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import moment from 'moment';
import _ from 'underscore';

import ClipboardListIcon from 'components/v2/icons/ClipboardListIcon';
import {
  getAppointmentStatusMetrics,
  cancelAppointment,
  updateAppointmentStatus,
  getAppointments,
  getAppointmentLocations,
} from 'utils/AppointmentWebAPIUtils';
import FetchWebAPIUtils from 'utils/FetchWebAPIUtils';
import { handleDataStream, convertJsonToCSV } from 'utils/StreamHandlingUtils';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import Container from 'components/v2/container';
import Pagination from 'components/v2/Pagination';
import { titleize } from 'underscore.string';
import SessionStore from '../../../stores/SessionStore';
import Filters from './Filters';
import AppointmentsList from './AppointmentsList';
import AppointmentCalendarMetrics from './AppointmentCalendarMetrics';
import ActiveFilters from './ActiveFilters';
import ProTip from '../../ui/ProTip';
import COLUMNS from './columns';
import CarrierValidationModal from './CarrierValidationModal';
import BulkContainerAvailabilityModal from './BulkContainerAvailabilityUploadModal';
import DropdownMenu from '../../v2/dropdowns/DropdownMenu';
import DropdownItem from '../../v2/dropdowns/DropdownItem';

const PER_PAGE = 20;
const DEFAULT_SORT_COLUMN = 'updates';
const ASCENDING = 'asc';
const DESCENDING = 'desc';

const FILTERS = {
  _ID: '_id',
  YARD: 'yard',
  STATUS: 'status',
  CONSIGNEE: 'consignee',
  SHIPPER: 'shipper',
  CARRIER: 'carrier',
  CONTAINER_NUMBER: 'container_number',
  MASTER_BILL: 'master_bill_number',
  PAGINATION: 'pagination',
  DATE_RANGE: 'daterange',
  WINDOW_START: 'window_start',
  WINDOW_END: 'window_end',
  CALENDAR: 'calendar',
  SHIPPER_REMOVE: 'shipper_remove',
  CARRIER_REMOVE: 'carrier_remove',
  CONSIGNEE_REMOVE: 'consignee_remove',
  SORT_BY_REMOVE: 'sort_by_remove',
  STATUS_REMOVE: 'status_remove',
  YARD_REMOVE: 'yard_remove',
  CONTAINER_REMOVE: 'container_remove',
  MASTER_BILL_REMOVE: 'master_bill_remove',
};

const ADMIN = 'Admin';
const CARRIER = 'Carrier';

export default function AppointmentModule() {
  const { t } = useTranslation();
  let history = useHistory();
  let abortController;

  const [showProTip, setShowPropTip] = useState(true);
  const [appointmentStatuses, setAppointmentStatuses] = useState([
    { name: t('APPROVED'), slug: 'approved' },
    { name: t('CANCELED'), slug: 'canceled' },
    { name: t('DECLINED'), slug: 'declined' },
    { name: t('PENDING'), slug: 'pending' },
    { name: t('COMPLETE'), slug: 'complete' },
    { name: t('MISSED'), slug: 'missed' },
  ]);

  const defaultQueryParams = {
    page: 1,
    perPage: PER_PAGE,
    sortBy: DEFAULT_SORT_COLUMN,
    sortOrder: DESCENDING,
  };

  function handleSessionChange() {
    const updatedUserType = SessionStore.getUserType();
    setUserType(updatedUserType);

    const mappedColumns = COLUMNS.filter((c) => {
      return c.show && c.users.includes(updatedUserType);
    });
    setColumns(mappedColumns);
  }

  useEffect(() => {
    handleSessionChange();
    SessionStore.addChangeListener(handleSessionChange);
    return () => {
      SessionStore.removeChangeListener(handleSessionChange);
    };
  }, []);

  const [userType, setUserType] = useState(SessionStore.getUserType());
  const [appointmentFacilities, setAppointmentFacilities] = useState([]);
  const [, setExporting] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [paginationCount, setPaginationCount] = useState(0);
  const [paginationPages, setPaginationPages] = useState(0);
  const [query, setQuery] = useState(defaultQueryParams);
  const [columns, setColumns] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [
    showBulkContainerAvailabilityModal,
    setShowBulkContainerAvailabilityModal,
  ] = useState(false);
  const [confirmationModalMessage, setConfirmationModalMessage] = useState('');
  const [confirmationModalAction, setConfirmationModalAction] = useState(
    () => {},
  );
  const [loaded, setLoaded] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [statusMetrics, setStatusMetrics] = useState([]);
  const [showCarrierValidationModal, setShowCarrierValidationModal] =
    useState(false);

  function transformQuery(query) {
    const ID_ITEMS = ['_id', 'yard', 'consignee', 'carrier', 'shipper'];

    let transformedQuery = {};
    _.map(query, (val, key) => {
      if (!_.isEmpty(val)) {
        if (key === 'status') {
          transformedQuery[key] = val.map((v) => {
            return v.slug;
          });
        } else if (ID_ITEMS.includes(key)) {
          transformedQuery[key] = val.map((v) => {
            return v._id;
          });
        } else {
          transformedQuery[key] = val;
        }
      } else if (key === 'page' || key === 'perPage') {
        transformedQuery[key] = val;
      }
    });
    return transformedQuery;
  }

  function onStatusActionClick(_id, newStatus) {
    const statusMap =
      newStatus === 'declined' && userType !== ADMIN ? 'canceled' : newStatus;
    setConfirmationModalMessage(
      t('MOVE_APPOINTMENT_STATUS_CONFIRMATION', { status: statusMap }),
    );
    setConfirmationModalAction(() => {
      return async function () {
        try {
          const result =
            statusMap === 'canceled'
              ? await cancelAppointment(_id)
              : await updateAppointmentStatus(_id, newStatus);
          if (result) {
            const newItems = appointments.map((a) => {
              if (a._id === result._id) {
                a.status = result.status;
              }
              return a;
            });
            setAppointments([...newItems]);
          }
        } finally {
          setShowConfirmationModal(false);
        }
      };
    });
    setShowConfirmationModal(true);
  }

  function onEditActionClick(_id) {
    history.push(`/appointments/${_id}`);
  }

  function onFilterChanged(type, value, toRemove) {
    switch (type) {
      case FILTERS.WINDOW_START: {
        setQuery({ ...query, ...value });
        break;
      }
      case FILTERS.WINDOW_END: {
        setQuery({ ...query, ...value });
        break;
      }
      case FILTERS.CALENDAR: {
        const { start: window_start, end: window_end, status } = value;
        const update = {
          window_start: moment(window_start).startOf('day').toISOString(),
          window_end: moment(window_end).endOf('day').toISOString(),
        };

        setSelectedCalendarDate(window_start);
        if (status) {
          update.status = [{ slug: status }];
        } else {
          update.status = undefined;
        }

        setQuery({
          ...query,
          ...update,
        });
        break;
      }
      case FILTERS.DATE_RANGE: {
        const { start, end } = value;
        if (!start && !end) {
          setQuery({ ..._.omit(query, ['window_start', 'window_end']) });
        } else {
          setQuery({
            ...query,
            window_start: moment(start).startOf('day').toISOString(),
            window_end: moment(end).endOf('day').toISOString(),
          });
        }
        break;
      }
      case FILTERS.PAGINATION: {
        setQuery({ ...query, page: value });
        break;
      }
      case FILTERS.YARD: {
        if (_.isEmpty(value)) {
          let filter = query;
          delete filter.yard;
          return setQuery({ ...filter });
        }
        const yardIds = value.map((v) => {
          return v._id;
        });
        setQuery({ ...query, yard: value });
        const appointmentFacilitiesUpdate = appointmentFacilities.map((s) => {
          s.selected = yardIds.includes(s._id);
          return s;
        });
        setAppointmentFacilities(appointmentFacilitiesUpdate);
        break;
      }
      case FILTERS.STATUS: {
        if (_.isEmpty(value)) {
          let filter = query;
          delete filter.status;
          return setQuery({ ...filter });
        }

        const selectedStatuses = value.map((v) => {
          return v.slug;
        });

        setQuery({ ...query, status: value });
        const appointmentStatusUpdate = appointmentStatuses.map((s) => {
          s.selected = selectedStatuses.includes(s.slug);
          return s;
        });
        setAppointmentStatuses(appointmentStatusUpdate);
        break;
      }
      case FILTERS.CONSIGNEE: {
        const consignees = query.consignee || [];
        consignees.push(value);
        setQuery({ ...query, consignee: consignees });
        break;
      }

      case FILTERS.SHIPPER: {
        const shippers = query.shipper || [];
        shippers.push(value);
        setQuery({ ...query, shipper: shippers });
        break;
      }
      case FILTERS.CARRIER: {
        const carriers = query.carrier || [];
        carriers.push(value);
        setQuery({ ...query, carrier: carriers });
        break;
      }
      case FILTERS.CONTAINER_NUMBER: {
        const containers = query.container_number || [];
        containers.push(value.container_number);
        setQuery({ ...query, container_number: containers });
        break;
      }
      case FILTERS.SORT_BY_REMOVE: {
        setQuery({ ...query, sortBy: undefined, sortOrder: undefined });
        break;
      }
      case FILTERS.STATUS_REMOVE: {
        let { status } = query;
        status =
          status && status.length
            ? status.filter((s) => {
                return s.slug !== toRemove;
              })
            : undefined;
        setQuery({ ...query, status });
        break;
      }
      case FILTERS.CONSIGNEE_REMOVE: {
        let { consignee } = query;
        consignee =
          consignee && consignee.length
            ? consignee.filter((c) => {
                return c._id !== toRemove;
              })
            : undefined;
        setQuery({ ...query, consignee });
        break;
      }
      case FILTERS.SHIPPER_REMOVE: {
        let { shipper } = query;
        shipper =
          shipper && shipper.length
            ? shipper.filter((s) => {
                return s._id !== toRemove;
              })
            : undefined;
        setQuery({ ...query, shipper });
        break;
      }
      case FILTERS.CARRIER_REMOVE: {
        let { carrier } = query;
        carrier =
          carrier && carrier.length
            ? carrier.filter((c) => {
                return c._id !== toRemove;
              })
            : undefined;
        setQuery({ ...query, carrier });
        break;
      }
      case FILTERS.YARD_REMOVE: {
        let { yard } = query;
        yard =
          yard && yard.length
            ? yard.filter((y) => {
                return y._id !== toRemove;
              })
            : undefined;
        setQuery({ ...query, yard });
        break;
      }
      case FILTERS.MASTER_BILL: {
        const masters = query.master_bill_number || [];
        masters.push(value.master_bill_number);
        setQuery({ ...query, master_bill_number: masters });
        break;
      }
      case FILTERS.CONTAINER_REMOVE: {
        let { container_number } = query;
        container_number =
          container_number && container_number.length > 0
            ? _.without(container_number, toRemove)
            : undefined;
        setQuery({ ...query, container_number: container_number });
        break;
      }
      case FILTERS.MASTER_BILL_REMOVE: {
        let { master_bill_number } = query;
        master_bill_number =
          master_bill_number && master_bill_number.length > 0
            ? _.without(master_bill_number, toRemove)
            : undefined;
        setQuery({ ...query, master_bill_number: master_bill_number });
        break;
      }
      default: {
        console.log('Unhandled Filter Change!', type, value);
      }
    }
  }

  function onHeaderClick(column) {
    // Columns eligible for sorting in configuration
    if (column.sort) {
      let newSortOrder = query.sortOrder;
      // If column is currently sorted, invert it
      if (query.sortBy === column.attribute) {
        newSortOrder = query.sortOrder === ASCENDING ? DESCENDING : ASCENDING;
      } else {
        // Set new column order
        newSortOrder = ASCENDING;
      }
      setQuery({ ...query, sortBy: column.attribute, sortOrder: newSortOrder });
    }
  }

  useEffect(() => {
    async function queryAppointmentFacilities() {
      const facilities = await getAppointmentLocations();
      if (checkForDifferentFacilities(facilities)) {
        setAppointmentFacilities(facilities);
      }
    }
    async function queryStatusMetrics() {
      const start = moment().subtract(1, 'days').startOf('day').toISOString();
      const end = moment().endOf('day').add(7, 'days').toISOString();
      const metrics = await getAppointmentStatusMetrics(start, end);
      setStatusMetrics(metrics);
    }
    queryAppointmentFacilities();
    queryStatusMetrics();
  }, [appointments]);

  const checkForDifferentFacilities = (facilities) => {
    if (!appointmentFacilities.length) {
      return true;
    }
    const fIds = appointmentFacilities.map((f) => f._id);
    return facilities.some((f) => !fIds.includes(f._id));
  };

  const debouncedQuery = useMemo(() => {
    return _.debounce(async (query) => {
      const filter = transformQuery(query);
      const result = await getAppointments(filter);
      if (result) {
        const { items, totalCount, totalPages } = result;
        setAppointments(items);
        setPaginationCount(totalCount);
        setPaginationPages(totalPages);
        setLoaded(true);
      } else {
        setPaginationCount(0);
        setPaginationPages(1);
        setAppointments([]);
        setLoaded(true);
      }
    }, 200);
  }, []);

  useEffect(() => {
    debouncedQuery(query);
  }, [query]);

  const hasAppointments = appointments && appointments.length > 0;

  async function exportCSV() {
    setExporting(true);
    if (abortController) {
      await abortController();
    }
    const mapQuery = () => {
      return Object.keys(query).reduce((finalQuery, key) => {
        if (['container_number', 'master_bill_number'].includes(key)) {
          finalQuery[key] = query[key].pop();
          return finalQuery;
        }
        if (['yard', 'consignee', 'shipper', 'carrier'].includes(key)) {
          finalQuery[key] = query[key]?.[0]?._id;
          return finalQuery;
        }
        if (key === 'status') {
          finalQuery[key] = query[key]?.[0]?.slug;
          return finalQuery;
        }
        finalQuery[key] = query[key];
        return finalQuery;
      }, {});
    };
    const { response, controller } =
      await FetchWebAPIUtils.getScheduledYardAppointmentsCSV(mapQuery());
    abortController = controller;
    handleDataStream(response, handleStreamEnd);
  }

  const handleStreamEnd = (rows) => {
    const filename = 'appointments.csv';
    setExporting(false);
    return convertJsonToCSV(rows, filename);
  };

  function onAppointmentClick() {
    if (userType !== CARRIER) {
      history.push('/appointments/new');
    } else {
      setShowCarrierValidationModal(true);
    }
  }

  function handleSelectAction(action) {
    switch (action) {
      case 'update_container_availability':
        return setShowBulkContainerAvailabilityModal(true);
      case 'export':
        return exportCSV();
      default:
        break;
    }
  }

  function handleClosePropTip() {
    setShowPropTip(false);
  }

  return (
    <Container className="pb-32" useMaxWidth={false}>
      <div className="flex flex-col space-y-4">
        <div className="mt-6 flex flex-wrap lg:-mx-4">
          <div className="lg:w-2/3 w-full mb-4 lg:mb-0 lg:px-4">
            {showProTip && (
              <ProTip
                message={t('PRO_TIP_APPOINTMENT_HEADER')}
                onClose={handleClosePropTip}
              />
            )}
          </div>
          <div className="lg:w-1/3 w-full flex justify-end space-x-2 lg:px-4">
            <div>
              <button
                className="button button-green flex inline"
                onClick={onAppointmentClick}
              >
                <ClipboardListIcon />
                {t('NEW_APPOINTMENT')}
              </button>
            </div>
            <DropdownMenu title={titleize(t('ACTIONS'))}>
              <Menu.Item id="export" key="export">
                <DropdownItem onClick={() => handleSelectAction('export')}>
                  {titleize(t('EXPORT'))}
                </DropdownItem>
              </Menu.Item>
              {userType === 'Admin' && (
                <Menu.Item key="update_container_availability">
                  <DropdownItem
                    onClick={() =>
                      handleSelectAction('update_container_availability')
                    }
                  >
                    {t('UPDATE_CONTAINER_AVAILABILITY')}
                  </DropdownItem>
                </Menu.Item>
              )}
            </DropdownMenu>
          </div>
        </div>
        <div className="flex mt-6">
          {false && (
            <div className="flex items-center cursor-pointer">
              <i className="fa fa-chevron-left" />
            </div>
          )}
          <div className="flex flex-1 overflow-x-auto space-x-2 sm:space-x-4">
            <AppointmentCalendarMetrics
              onCalendarClick={(data) => onFilterChanged('calendar', data)}
              selectedDate={selectedCalendarDate}
              metrics={statusMetrics}
            />
          </div>
          {false && (
            <div className="flex items-center cursor-pointer">
              <i className="fa fa-chevron-right" />
            </div>
          )}
        </div>
        <div className="p-4 rounded bg-grey-light">
          {loaded && (
            <Filters
              onFilterChanged={onFilterChanged}
              appointmentStatuses={appointmentStatuses}
              appointmentFacilities={appointmentFacilities}
            />
          )}
        </div>
        <div className="flex">
          <ActiveFilters
            query={query}
            onRemove={onFilterChanged}
            onClear={() => setQuery({})}
          />
        </div>
        {hasAppointments && (
          <div>
            <Pagination
              key={1}
              perPage={PER_PAGE}
              page={query.page}
              pages={paginationPages}
              total={paginationCount}
              onPageClick={(page) => onFilterChanged('pagination', page)}
            />
            <AppointmentsList
              sortColumn={query.sortBy}
              sortOrder={query.sortOrder}
              appointments={appointments}
              userType={userType}
              columns={columns}
              onHeaderClick={onHeaderClick}
              onStatusActionClick={onStatusActionClick}
              onEditActionClick={onEditActionClick}
            />
          </div>
        )}
        {loaded && !hasAppointments && (
          <div className="p-4">{t('NO_APPOINTMENTS_FOUND')}</div>
        )}
        {!loaded && (
          <div className="flex w-full justify-center align-middle">
            <i className="fa fa-spinner fa-spin text-5xl text-green" />
          </div>
        )}
        <ConfirmModal
          show={showConfirmationModal}
          title={titleize(t('CONFIRM'))}
          message={confirmationModalMessage}
          onConfirm={() => confirmationModalAction()}
          onHide={() => setShowConfirmationModal(false)}
          affirmative={titleize(t('CONFIRM'))}
          hideButtonText={titleize(t('CANCEL'))}
          primaryButtonColor="green"
        />
        <CarrierValidationModal
          show={showCarrierValidationModal}
          onHideModal={() => setShowCarrierValidationModal(false)}
        />
        <BulkContainerAvailabilityModal
          showModal={showBulkContainerAvailabilityModal}
          onHide={() => setShowBulkContainerAvailabilityModal(false)}
        />
      </div>
    </Container>
  );
}
